import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AssetAggregateSummary } from '../../api/asset.api';
import { useGetAssetAggregateSummaryQuery } from '../../api/asset.api';
import { useGetCurrentUserQuery } from '../../api/user.api.ts';
import InfoCard from '../../components/InfoCard/InfoCard';
import { ALL_ASSETS, ALL_CONTRACTS, SUMMARY, TOTAL_EQUIPMENT_COST } from '../../constants/messages.tsx';
import {
	clearSelectedViewAllChart,
	selectSelectedViewAllChart,
} from '../../redux/slices/summaryGraphViewSlice.ts';
import type { RootState } from '../../redux/store';
import { FormatCurrency, FormatCurrencyCompact } from '../../utils/helpers';
import SummaryGraphContainer from './Components/SummaryGraphContainer.tsx';
import ViewAllChart from './Components/ViewAllChart/ViewAllChart';
import { selectAccount } from '../../redux/slices/accountSlice.ts';

const Summary: React.FC = () => {
	const dispatch = useDispatch();
	const { data: user } = useGetCurrentUserQuery();
	const [isViewAllChartOpen, setIsViewAllChartOpen] = useState(false);
	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);
	const account = useSelector(selectAccount)
	const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

	const viewAllSelected = useSelector((state: RootState) => selectSelectedViewAllChart(state));

	useEffect(() => {
		if (!!viewAllSelected) {
			setIsViewAllChartOpen(true);
		} else {
			setIsViewAllChartOpen(false);
		}
	}, [viewAllSelected]);

	const handleCloseViewAllChartModal = () => {
		setIsViewAllChartOpen(false);
		dispatch(clearSelectedViewAllChart());
	};

	const {
		data: aggregateSummary = {} as AssetAggregateSummary,
		isLoading: summaryLoading,
		isError: summaryError,
		refetch: refetchSummary,
	} = useGetAssetAggregateSummaryQuery(
		{ sourceId: account?.sourceId ?? 0, accountId: account?.accountId ?? 0, accountTypeCode: account?.accountTypeCode ?? '', filters: activeFilters },
		{ skip: Object.keys(account).length === 0 || !account }
	);

	useEffect(() => {
		if(!!user && Object.keys(account).length !== 0) {
			refetchSummary();
		}
	}, [account]);

	const getSummaryValue = (
		key: keyof AssetAggregateSummary,
		formatCurrency: boolean
	): string | number => {
		if (summaryLoading) return 'Loading...';
		if (summaryError) return 'Error';
		const value = aggregateSummary[key] ?? 'N/A';

		if (!formatCurrency || !(typeof value === 'number')) {
			return value
		}

		// If we have currency at or above $10,000,000 (8 digits or more) show in compact form of $10M to avoid text overflowing card
		const absValue = Math.abs(value);

		if (absValue >= 10_000_000) {
			return FormatCurrencyCompact(value);
		}

        return FormatCurrency(value, { rounding: true });
	};

	return (
		<Box>
			{/* Title */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
				<Typography
					variant="h4"
					component="h1"
					sx={{ 
						color: (theme) => theme.palette.brand.primary, 
						fontWeight: 'bold',
						ml: '0.75rem',
					}}
				>
					{SUMMARY}
				</Typography>
			</Box>
			<Grid
				container
				spacing={2}
				sx={{
					mb: 3,
					display: 'flex',
					flexDirection: 'row',
					flexWrap: 'wrap',
					justifyContent: isMobile ? 'space-evenly' : 'flex-start',
					alignItems: 'stretch',
					margin: isMobile ? '1rem auto' : undefined,
				}}
			>
				<Grid size={{ xs: 6, md: 4, lg: 3 }} data-testid="info-total-equipment-cost-card"
>
					<InfoCard
						heading={getSummaryValue('totalEquipmentCost', true)}
						subheading={TOTAL_EQUIPMENT_COST}
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }} data-testid="info-total-contracts-card">
					<InfoCard
						heading={getSummaryValue('totalContracts', false)}
						subheading={ALL_CONTRACTS}
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
				<Grid size={{ xs: 6, md: 4, lg: 3 }} data-testid="info-total-assets-card">
					<InfoCard
						heading={getSummaryValue('totalAssets', false)}
						subheading={ALL_ASSETS}
						sx={{ flexGrow: 1 }}
					></InfoCard>
				</Grid>
			</Grid>
			<SummaryGraphContainer />

			<ViewAllChart open={isViewAllChartOpen} onClose={() => handleCloseViewAllChartModal()} />
		</Box>
	);
};

export default Summary;
